import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../services/api/client.js';
import { formatAppDate } from '../utils/locale.js';
import { friendlyErrorMessage } from '../lib/notifications.js';

const ContentFeedContext = createContext(null);
const ACTIVE_BATCH_STATUSES = new Set(['queued', 'generating', 'reserved']);
const CONTENT_PAGE_LIMIT = 12;
const SETTLE_POLL_INTERVAL_MS = 4000;
const SETTLE_POLL_TICKS = 10;
const PUBLISH_POLL_INTERVAL_MS = 4000;
const PUBLISH_POLL_TICKS = 30;
const PUBLISH_POLL_LOOKAHEAD_MS = 60 * 1000;
const ACTIVE_METRICS_USER_BUDGET = 3;
const ACTIVE_METRICS_USER_WINDOW_MS = 60 * 1000;
const ACTIVE_METRICS_CONTENT_COOLDOWN_MS = 60 * 1000;
const ACTIVE_METRICS_REFRESH_DELAY_MS = 2500;
const ACTIVE_VIEW_METRIC_PLATFORMS = new Set(['instagram', 'facebook', 'threads', 'telegram']);
const PENDING_METRICS_REPAINT_FAST_MS = 15 * 1000;
const PENDING_METRICS_REPAINT_SLOW_MS = 60 * 1000;
const PENDING_METRICS_REPAINT_MAX_MS = 10 * 60 * 1000;

export function ContentFeedProvider({ children }) {
  const [content, setContent] = useState([]);
  const [activeBatches, setActiveBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [counts, setCounts] = useState(() => emptyContentCounts());
  const [statusFilter, setStatusFilter] = useState('all');
  const contentRef = useRef([]);
  const statusFilterRef = useRef('all');
  const settlePollRef = useRef({ timeoutId: null, remaining: 0 });
  const publishPollRef = useRef({ timeoutId: null, remaining: 0, activeSignature: '' });
  const activeMetricsRef = useRef({ windowStartedAt: 0, count: 0, byContentId: new Map(), refreshTimeoutId: null });
  const pendingMetricsRef = useRef({ timeoutId: null, signature: '', startedAt: 0, tick: 0 });

  async function refreshContent({ background = false, refreshMetrics = true, status = statusFilterRef.current } = {}) {
    const nextStatusFilter = normalizeFeedStatus(status);
    const statusChanged = nextStatusFilter !== statusFilterRef.current;
    if (statusChanged) {
      statusFilterRef.current = nextStatusFilter;
      setStatusFilter(nextStatusFilter);
      contentRef.current = [];
      setContent([]);
      setNextCursor(null);
    }
    try {
      if (!background || statusChanged) setLoading(true);
      setError(null);
      const feed = await fetchContentFeedRows({ limit: CONTENT_PAGE_LIMIT, status: nextStatusFilter });
      const rows = feed.rows;
      const activeBatchIds = activeBatchIdsForRows(rows);
      const displayBatchIds = displayBatchIdsForRows(rows);
      const feedBatchById = new Map(feed.batches.map(batch => [batch.id, batch]));
      const missingBatchIds = new Set([...displayBatchIds].filter(id => !feedBatchById.has(id)));
      const missingBatches = await fetchBatchesForIds(missingBatchIds);
      const batches = [...feed.batches, ...missingBatches];
      const batchById = new Map(batches.map(batch => [batch.id, batch]));
      const fetchedContent = rows.map(row => {
        const item = row.contentItem || row;
        return mapContentRow(row, row.publishSummary || null, batchById.get(item.generationBatchId) || null);
      });
      const nextContent = background && !statusChanged ? mergeContentForBackground(contentRef.current, fetchedContent) : fetchedContent;
      // Keep every batch that's still in an ACTIVE status from `previous` —
      // don't drop a freshly-created batch just because its items haven't
      // been ingested into this feed snapshot yet (pagination / race /
      // text-saved-but-visual-cooking transitions can all make the feed
      // temporarily exclude items even though the batch is still alive).
      // The merge with `batches` below will reconcile counts/status; the
      // sortActiveBatches step inside mergeFetchedBatches drops anything
      // that has actually transitioned to a terminal status.
      setActiveBatches(previous => mergeFetchedBatches(previous, batches));
      contentRef.current = nextContent;
      setContent(nextContent);
      setNextCursor(feed.nextCursor || null);
      setCounts(normalizeContentCounts(feed.counts, nextContent));
      updatePublishPolling(nextContent);
      updatePendingMetricsRepaint(nextContent);
      if (refreshMetrics) requestActiveMetricsRefresh(nextContent);
      return { content: nextContent, batches, activeBatchIds };
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load the Library. Refresh in a moment.'));
      return null;
    } finally {
      if (!background || statusChanged) setLoading(false);
    }
  }

  async function loadMore() {
    if (!nextCursor || loadingMore) return null;
    setLoadingMore(true);
    setError(null);
    try {
      const feed = await fetchContentFeedRows({ limit: CONTENT_PAGE_LIMIT, cursor: nextCursor, status: statusFilterRef.current });
      const feedBatchById = new Map(feed.batches.map(batch => [batch.id, batch]));
      const displayBatchIds = displayBatchIdsForRows(feed.rows);
      const missingBatchIds = new Set([...displayBatchIds].filter(id => !feedBatchById.has(id)));
      const missingBatches = await fetchBatchesForIds(missingBatchIds);
      const batchById = new Map([...feed.batches, ...missingBatches].map(batch => [batch.id, batch]));
      const fetchedContent = feed.rows.map(row => {
        const item = row.contentItem || row;
        return mapContentRow(row, row.publishSummary || null, batchById.get(item.generationBatchId) || null);
      });
      const existingIds = new Set(contentRef.current.map(item => item.id));
      const dedup = fetchedContent.filter(item => !existingIds.has(item.id));
      const merged = [...contentRef.current, ...dedup];
      contentRef.current = merged;
      setContent(merged);
      setNextCursor(feed.nextCursor || null);
      if (feed.counts) setCounts(normalizeContentCounts(feed.counts, merged));
      return { added: dedup.length };
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load more posts. Try again.'));
      return null;
    } finally {
      setLoadingMore(false);
    }
  }

  async function hydrateContentItem(id) {
    if (!id) return null;
    const full = await api.content.get(id);
    const previous = contentRef.current.find(item => item.id === id) || null;
    const previousRaw = previous?.raw || null;
    const mergedRaw = mergeRawContentForMedia(previousRaw, full);
    const mapped = mapContentRow(
      mergedRaw,
      mergedRaw.publishSummary || previous?.publishSummary || null,
      previous?.batch || null
    );
    const nextItem = mergeContentItem(previous, mapped);
    const exists = contentRef.current.some(item => item.id === id);
    const nextContent = exists
      ? contentRef.current.map(item => item.id === id ? nextItem : item)
      : [nextItem, ...contentRef.current];
    contentRef.current = nextContent;
    setContent(nextContent);
    return nextItem.raw;
  }

  function upsertContentItems(items, batch = null) {
    if (!Array.isArray(items) || items.length === 0) return;
    const mapped = items.map(row => mapContentRow(row, row?.publishSummary || null, batch));
    const nextContent = mergeUpsertedContent(contentRef.current, mapped);
    contentRef.current = nextContent;
    setContent(nextContent);
  }

  function startGenerationBatch(batch, items) {
    if (batch) {
      setActiveBatches(previous => upsertBatch(previous, batch));
    }
    upsertContentItems(items, batch);
  }

  async function pollActiveBatches() {
    const active = activeBatches.filter(batch => ACTIVE_BATCH_STATUSES.has(batch.status));
    if (!active.length) return;
    const previousActiveIds = new Set(active.map(batch => batch.id));
    const result = await refreshContent({ background: true });
    if (!result) return;
    const settled = [...previousActiveIds].some(id => {
      const batch = result.batches.find(item => item.id === id);
      return !result.activeBatchIds.has(id) || (batch && !ACTIVE_BATCH_STATUSES.has(batch.status));
    });
    if (settled) {
      startSettlePolling();
    }
  }

  function startSettlePolling() {
    settlePollRef.current.remaining = Math.max(settlePollRef.current.remaining, SETTLE_POLL_TICKS);
    if (settlePollRef.current.timeoutId) return;
    scheduleSettlePollTick();
  }

  function scheduleSettlePollTick() {
    if (settlePollRef.current.remaining <= 0) return;
    settlePollRef.current.timeoutId = window.setTimeout(async () => {
      settlePollRef.current.timeoutId = null;
      settlePollRef.current.remaining -= 1;
      await refreshContent({ background: true });
      scheduleSettlePollTick();
    }, SETTLE_POLL_INTERVAL_MS);
  }

  function updatePublishPolling(items) {
    const signature = duePublishingSignature(items);
    if (!signature) {
      publishPollRef.current.activeSignature = '';
      publishPollRef.current.remaining = 0;
      if (publishPollRef.current.timeoutId) {
        window.clearTimeout(publishPollRef.current.timeoutId);
        publishPollRef.current.timeoutId = null;
      }
      return;
    }

    if (publishPollRef.current.activeSignature !== signature) {
      publishPollRef.current.activeSignature = signature;
      publishPollRef.current.remaining = PUBLISH_POLL_TICKS;
    }
    if (!publishPollRef.current.timeoutId && publishPollRef.current.remaining > 0) {
      schedulePublishPollTick();
    }
  }

  function schedulePublishPollTick() {
    if (publishPollRef.current.remaining <= 0) return;
    publishPollRef.current.timeoutId = window.setTimeout(async () => {
      publishPollRef.current.timeoutId = null;
      publishPollRef.current.remaining -= 1;
      await refreshContent({ background: true });
      if (!publishPollRef.current.timeoutId && publishPollRef.current.remaining > 0) {
        schedulePublishPollTick();
      }
    }, PUBLISH_POLL_INTERVAL_MS);
  }

  function requestActiveMetricsRefresh(items) {
    if (typeof window === 'undefined') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (!isContentRouteActive()) return;
    const candidates = items.filter(shouldRefreshActiveMetrics).slice(0, ACTIVE_METRICS_USER_BUDGET);
    for (const item of candidates) {
      if (!consumeActiveMetricsBudget(item.id)) continue;
      api.content.refreshMetrics(item.id, { limit: 5 })
        .then(() => scheduleMetricsRefreshReload())
        .catch(() => {});
    }
  }

  function isContentRouteActive() {
    const path = window.location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
    return path === '/content' || path.startsWith('/content/');
  }

  function consumeActiveMetricsBudget(contentId) {
    const now = Date.now();
    const state = activeMetricsRef.current;
    if (now - state.windowStartedAt >= ACTIVE_METRICS_USER_WINDOW_MS) {
      state.windowStartedAt = now;
      state.count = 0;
    }
    if (state.count >= ACTIVE_METRICS_USER_BUDGET) return false;
    const previous = state.byContentId.get(contentId) || 0;
    if (now - previous < ACTIVE_METRICS_CONTENT_COOLDOWN_MS) return false;
    state.count += 1;
    state.byContentId.set(contentId, now);
    return true;
  }

  function scheduleMetricsRefreshReload() {
    if (activeMetricsRef.current.refreshTimeoutId) return;
    activeMetricsRef.current.refreshTimeoutId = window.setTimeout(async () => {
      activeMetricsRef.current.refreshTimeoutId = null;
      await refreshContent({ background: true, refreshMetrics: false });
    }, ACTIVE_METRICS_REFRESH_DELAY_MS);
  }

  function updatePendingMetricsRepaint(items) {
    if (!canPassiveMetricsRepaint()) {
      stopPendingMetricsRepaint();
      return;
    }
    const signature = pendingViewMetricSignature(items);
    if (!signature) {
      stopPendingMetricsRepaint();
      return;
    }

    const state = pendingMetricsRef.current;
    const now = Date.now();
    if (state.signature !== signature) {
      if (state.timeoutId) window.clearTimeout(state.timeoutId);
      state.timeoutId = null;
      state.signature = signature;
      state.startedAt = now;
      state.tick = 0;
    }
    if (now - state.startedAt >= PENDING_METRICS_REPAINT_MAX_MS) {
      stopPendingMetricsRepaint();
      return;
    }
    if (!state.timeoutId) schedulePendingMetricsRepaintTick();
  }

  function schedulePendingMetricsRepaintTick() {
    const state = pendingMetricsRef.current;
    const delay = state.tick === 0 ? PENDING_METRICS_REPAINT_FAST_MS : PENDING_METRICS_REPAINT_SLOW_MS;
    state.timeoutId = window.setTimeout(async () => {
      state.timeoutId = null;
      if (!canPassiveMetricsRepaint()) {
        stopPendingMetricsRepaint();
        return;
      }
      state.tick += 1;
      await refreshContent({ background: true, refreshMetrics: false });
    }, delay);
  }

  function stopPendingMetricsRepaint() {
    const state = pendingMetricsRef.current;
    if (state.timeoutId) window.clearTimeout(state.timeoutId);
    state.timeoutId = null;
    state.signature = '';
    state.startedAt = 0;
    state.tick = 0;
  }

  function canPassiveMetricsRepaint() {
    if (typeof window === 'undefined') return false;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
    return isContentRouteActive();
  }

  useEffect(() => {
    refreshContent();
    const handleVisibleRefresh = () => {
      if (canPassiveMetricsRepaint() && pendingViewMetricSignature(contentRef.current)) {
        refreshContent({ background: true, refreshMetrics: false });
      }
    };
    window.addEventListener('focus', handleVisibleRefresh);
    document.addEventListener('visibilitychange', handleVisibleRefresh);
    return () => {
      window.removeEventListener('focus', handleVisibleRefresh);
      document.removeEventListener('visibilitychange', handleVisibleRefresh);
      if (settlePollRef.current.timeoutId) {
        window.clearTimeout(settlePollRef.current.timeoutId);
      }
      if (publishPollRef.current.timeoutId) {
        window.clearTimeout(publishPollRef.current.timeoutId);
      }
      if (activeMetricsRef.current.refreshTimeoutId) {
        window.clearTimeout(activeMetricsRef.current.refreshTimeoutId);
      }
      stopPendingMetricsRepaint();
    };
  }, []);

  useEffect(() => {
    if (!activeBatches.some(batch => ACTIVE_BATCH_STATUSES.has(batch.status))) return undefined;
    const intervalId = window.setInterval(() => {
      pollActiveBatches();
    }, 4000);
    return () => window.clearInterval(intervalId);
  }, [activeBatches]);

  const value = {
    content,
    activeBatches,
    loading,
    loadingMore,
    error,
    counts,
    statusFilter,
    nextCursor,
    hasMore: Boolean(nextCursor),
    refreshContent,
    loadMore,
    hydrateContentItem,
    startGenerationBatch,
    upsertContentItems,
    pollActiveBatches,
  };

  return <ContentFeedContext.Provider value={value}>{children}</ContentFeedContext.Provider>;
}

async function fetchContentFeedRows({ limit = CONTENT_PAGE_LIMIT, cursor = null, status = 'all' } = {}) {
  try {
    const filters = { limit, view: 'list' };
    const normalizedStatus = normalizeFeedStatus(status);
    if (normalizedStatus !== 'all') filters.status = normalizedStatus;
    if (cursor) filters.cursor = cursor;
    const response = await api.content.feed(filters);
    return {
      rows: response.content || [],
      batches: response.batches || [],
      counts: response.counts || null,
      nextCursor: response.nextCursor || null
    };
  } catch (err) {
    if (err?.status !== 404) throw err;
  }

  const listFilters = { limit };
  const normalizedStatus = normalizeFeedStatus(status);
  if (normalizedStatus !== 'all') listFilters.status = normalizedStatus;
  if (cursor) listFilters.cursor = cursor;
  const [response, scheduleResponse, publishedScheduleResponse] = await Promise.all([
    api.content.list(listFilters),
    api.scheduler.list({ limit: 100 }),
    api.scheduler.list({ limit: 100, status: 'published' }),
  ]);
  const scheduleByContent = buildScheduleByContent(mergeSchedulePosts(scheduleResponse.posts || [], publishedScheduleResponse.posts || []));
  const rows = (response.content || []).map(row => {
    const item = row.contentItem || row;
    return { ...row, publishSummary: scheduleByContent.get(item.id) || null };
  });
  return { rows, batches: [], counts: null, nextCursor: response.nextCursor || null };
}

export function useContentFeedContext() {
  const context = useContext(ContentFeedContext);
  if (!context) {
    throw new Error('useContentFeed must be used inside ContentFeedProvider');
  }
  return context;
}

function mergeContentForBackground(previous, incoming) {
  if (!previous.length) return incoming;
  const previousById = new Map(previous.map(item => [item.id, item]));
  const incomingIds = new Set(incoming.map(item => item.id));
  const refreshed = incoming.map(item => mergeContentItem(previousById.get(item.id), item));
  const previousOnly = previous.filter(item => !incomingIds.has(item.id));
  return [...refreshed, ...previousOnly];
}

function mergeUpsertedContent(previous, incoming) {
  const previousById = new Map(previous.map(item => [item.id, item]));
  const incomingById = new Map(incoming.map(item => [item.id, item]));
  const incomingIds = new Set(incoming.map(item => item.id));
  const newItems = incoming.filter(item => !previousById.has(item.id));
  const existing = previous.map(item => incomingIds.has(item.id) ? mergeContentItem(item, incomingById.get(item.id)) : item);
  return [...newItems, ...existing];
}

function mergeContentItem(previous, incoming) {
  if (!previous) return incoming;
  if (!incoming) return previous;
  const merged = { ...previous, ...incoming };
  const previousMediaScore = loadedMediaScore(previous);
  const incomingMediaScore = loadedMediaScore(incoming);
  if (previousMediaScore > incomingMediaScore) {
    merged.imageUrl = previous.imageUrl || incoming.imageUrl;
    merged.imageUrls = previous.imageUrls?.length ? previous.imageUrls : incoming.imageUrls;
    merged.mediaPreview = mergeMediaPreview(incoming.mediaPreview, previous.mediaPreview);
    merged.raw = mergeRawContentForMedia(incoming.raw, previous.raw);
  }
  return merged;
}

function emptyContentCounts() {
  return {
    all: 0,
    generating: 0,
    ready: 0,
    planned: 0,
    scheduled: 0,
    publishing: 0,
    published: 0,
    draft: 0,
    failed: 0,
  };
}

function normalizeFeedStatus(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : 'all';
}

function normalizeContentCounts(counts, fallbackItems = []) {
  const next = emptyContentCounts();
  if (counts && typeof counts === 'object') {
    Object.entries(counts).forEach(([key, value]) => {
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric >= 0) next[key] = numeric;
    });
    return next;
  }

  fallbackItems.forEach(item => {
    const status = item?.status || 'draft';
    next[status] = (next[status] || 0) + 1;
    next.all += 1;
  });
  return next;
}

function mergeRawContentForMedia(base, mediaSource) {
  if (!base) return mediaSource;
  if (!mediaSource) return base;
  const baseSlides = Array.isArray(base.carouselSlides) ? base.carouselSlides : [];
  const sourceSlides = Array.isArray(mediaSource.carouselSlides) ? mediaSource.carouselSlides : [];
  const baseUrls = Array.isArray(base.mediaUrls) ? base.mediaUrls : [];
  const sourceUrls = Array.isArray(mediaSource.mediaUrls) ? mediaSource.mediaUrls : [];
  const useSourceMedia = sourceSlides.length + sourceUrls.length > baseSlides.length + baseUrls.length;
  const media = useSourceMedia ? mediaSource : base;
  return {
    ...base,
    ...mediaSource,
    contentItem: { ...(base.contentItem || {}), ...(mediaSource.contentItem || {}) },
    activeVersion: {
      ...(base.activeVersion || {}),
      ...(mediaSource.activeVersion || {}),
      mediaUrls: media.activeVersion?.mediaUrls || media.mediaUrls || [],
      mediaPreview: mergeMediaPreview(mediaSource.activeVersion?.mediaPreview, media.activeVersion?.mediaPreview || media.mediaPreview),
    },
    carouselSlides: media.carouselSlides || [],
    mediaUrls: media.mediaUrls || media.activeVersion?.mediaUrls || [],
    mediaPreview: mergeMediaPreview(base.mediaPreview, mediaSource.mediaPreview),
    publishSummary: base.publishSummary || mediaSource.publishSummary || null,
    generationBatch: base.generationBatch || mediaSource.generationBatch || null,
    publishPlatforms: base.publishPlatforms || mediaSource.publishPlatforms,
    publishTargetAccountIds: base.publishTargetAccountIds || mediaSource.publishTargetAccountIds,
    publishMode: base.publishMode || mediaSource.publishMode,
    publishScheduleAt: base.publishScheduleAt || mediaSource.publishScheduleAt,
  };
}

function loadedMediaScore(item) {
  const raw = item?.raw || item || {};
  return Math.max(
    Array.isArray(item?.imageUrls) ? item.imageUrls.length : 0,
    Array.isArray(raw.carouselSlides) ? raw.carouselSlides.length : 0,
    Array.isArray(raw.mediaUrls) ? raw.mediaUrls.length : 0,
    Array.isArray(raw.activeVersion?.mediaUrls) ? raw.activeVersion.mediaUrls.length : 0
  );
}

function mergeMediaPreview(base, next) {
  return {
    ...(base || {}),
    ...(next || {}),
    count: Math.max(Number(base?.count || 0), Number(next?.count || 0)),
    hasCarousel: Boolean(base?.hasCarousel || next?.hasCarousel || Number(base?.count || 0) > 1 || Number(next?.count || 0) > 1),
  };
}

function upsertBatch(previous, batch) {
  const byId = new Map(previous.map(item => [item.id, item]));
  byId.set(batch.id, { ...byId.get(batch.id), ...batch });
  return sortActiveBatches(Array.from(byId.values()));
}

function mergeFetchedBatches(previous, batches) {
  const byId = new Map(previous.map(item => [item.id, item]));
  batches.forEach(batch => byId.set(batch.id, { ...byId.get(batch.id), ...batch }));
  return sortActiveBatches(Array.from(byId.values()));
}

function sortActiveBatches(batches) {
  return batches
    .filter(batch => ACTIVE_BATCH_STATUSES.has(batch.status))
    .sort((a, b) => Date.parse(b.createdAt || 0) - Date.parse(a.createdAt || 0));
}

function mergeSchedulePosts(...groups) {
  const byId = new Map();
  groups.flat().forEach(row => {
    const post = row?.post || row;
    if (!post?.id) return;
    byId.set(post.id, { ...byId.get(post.id), ...row });
  });
  return Array.from(byId.values());
}

function timestamp(value) {
  const parsed = Date.parse(value || '');
  return Number.isNaN(parsed) ? 0 : parsed;
}

function duePublishingSignature(items) {
  const now = Date.now();
  return items
    .filter(item => ['scheduled', 'publishing'].includes(item.status))
    .filter(item => item.publishSummary?.activeCount > 0)
    .filter(item => item.status === 'publishing' || timestamp(item.scheduledAt) <= now + PUBLISH_POLL_LOOKAHEAD_MS)
    .map(item => item.id)
    .sort()
    .join('|');
}

function shouldRefreshActiveMetrics(item) {
  if (item.status !== 'published') return false;
  const targets = Array.isArray(item.publishSummary?.targets) ? item.publishSummary.targets : [];
  const publishedTargets = targets.filter(target => target.status === 'published');
  if (!publishedTargets.length) return false;
  return publishedTargets.some(shouldRefreshTargetMetrics);
}

function shouldRefreshTargetMetrics(target) {
  const publishedAt = timestamp(target.publishedAt);
  if (!publishedAt) return false;
  const metrics = target.metrics || target.analytics || target.insights || target.stats || null;
  const fetchedAt = timestamp(metrics?.fetchedAt);
  if (!fetchedAt) return true;
  if (shouldRefreshMissingViewMetric(target, metrics, publishedAt, fetchedAt)) return true;
  return Date.now() - fetchedAt >= activeMetricStaleMs(publishedAt);
}

function pendingViewMetricSignature(items) {
  const now = Date.now();
  return items
    .filter(item => item.status === 'published')
    .flatMap(item => pendingViewMetricKeysForItem(item, now))
    .sort()
    .join('|');
}

function pendingViewMetricKeysForItem(item, now) {
  const targets = Array.isArray(item.publishSummary?.targets) ? item.publishSummary.targets : [];
  return targets
    .filter(target => shouldPassiveRepaintTargetMetrics(target, now))
    .map(target => target.id || `${item.id}:${target.platform}:${target.publishedAt || ''}`);
}

function shouldPassiveRepaintTargetMetrics(target, now) {
  if (target.status !== 'published') return false;
  if (!ACTIVE_VIEW_METRIC_PLATFORMS.has(String(target.platform || '').toLowerCase())) return false;
  const publishedAt = timestamp(target.publishedAt);
  if (!publishedAt) return false;
  const ageMs = Math.max(0, now - publishedAt);
  if (ageMs >= 24 * 60 * 60 * 1000) return false;

  const metrics = target.metrics || target.analytics || target.insights || target.stats || null;
  if (!metrics || metrics.viewsPending === true || metrics.viewsAvailable === false) return true;
  const views = metricValue(metrics, ['views', 'viewCount', 'viewsCount', 'impressions', 'impressionCount', 'impressionsCount', 'reach', 'reachCount']);
  if (views === null) return true;
  return views === 0 && ageMs < 2 * 60 * 60 * 1000;
}

function shouldRefreshMissingViewMetric(target, metrics, publishedAt, fetchedAt) {
  if (!metrics || !ACTIVE_VIEW_METRIC_PLATFORMS.has(String(target.platform || '').toLowerCase())) return false;
  const views = metricValue(metrics, ['views', 'viewCount', 'viewsCount', 'impressions', 'impressionCount', 'impressionsCount', 'reach', 'reachCount']);
  if (views !== null && views > 0) return false;
  const hasEngagement = [
    metricValue(metrics, ['likes', 'likeCount', 'likesCount']),
    metricValue(metrics, ['comments', 'commentCount', 'commentsCount', 'replies', 'replyCount', 'repliesCount']),
    reactionMetricValue(metrics),
  ].some(value => value !== null && value > 0);
  const ageMs = Math.max(0, Date.now() - publishedAt);
  if (ageMs >= 24 * 60 * 60 * 1000 && !hasEngagement) return false;
  return Date.now() - fetchedAt >= missingViewMetricStaleMs(publishedAt);
}

function missingViewMetricStaleMs(publishedAt) {
  const ageMs = Math.max(0, Date.now() - publishedAt);
  if (ageMs < 24 * 60 * 60 * 1000) return 2 * 60 * 1000;
  return 15 * 60 * 1000;
}

function activeMetricStaleMs(publishedAt) {
  const ageMs = Math.max(0, Date.now() - publishedAt);
  if (ageMs < 10 * 60 * 1000) return 60 * 1000;
  if (ageMs < 60 * 60 * 1000) return 5 * 60 * 1000;
  if (ageMs < 24 * 60 * 60 * 1000) return 15 * 60 * 1000;
  return 60 * 60 * 1000;
}

function activeBatchIdsForRows(rows) {
  return new Set(rows
    .map(row => row.contentItem || row)
    .filter(item => item?.generationBatchId && isActiveGeneratingItem(item))
    .map(item => item.generationBatchId));
}

function displayBatchIdsForRows(rows) {
  return new Set(rows
    .map(row => row.contentItem || row)
    .filter(item => item?.generationBatchId && shouldUseBatchForDisplay(item))
    .map(item => item.generationBatchId));
}

function shouldUseBatchForDisplay(item) {
  if (isActiveGeneratingItem(item)) return true;
  if (item.status === 'failed') return true;
  return item.generationStatus === 'failed';
}

function isActiveGeneratingItem(item) {
  if (!item) return false;
  if (item.status === 'generating') return true;
  return Boolean(item.generationStatus && !['ready', 'failed'].includes(item.generationStatus));
}

async function fetchBatchesForIds(batchIds) {
  if (!batchIds.size) return [];
  const results = await Promise.allSettled([...batchIds].map(id => api.content.getBatch(id)));
  return results
    .filter(result => result.status === 'fulfilled' && result.value?.batch)
    .map(result => result.value.batch);
}

function mapContentRow(row, publishSummary = null, batch = null) {
  const item = row.contentItem || row;
  const version = row.activeVersion || null;
  const batchPlatforms = batch?.platforms || [];
  const platforms = preferredPlatforms(publishSummary, version);
  // Per-item job platforms, surfaced by content-worker so we don't have to
  // guess. For a language-split batch (e.g. IG-EN + TG-UK), each item's
  // jobPlatforms contains ONLY the platform(s) for its language group.
  // Without this we'd fall back to batch.platforms (the full IG+TG superset)
  // and render 4 tiles for a 2-item batch.
  const jobPlatforms = Array.isArray(row?.jobPlatforms) ? row.jobPlatforms : [];
  // Priority order: real publish_targets → per-item job platforms → batch superset
  const platformList = platforms.length
    ? platforms
    : (jobPlatforms.length ? jobPlatforms : batchPlatforms);
  const fallbackPlatform = item.format === 'image_post' ? 'instagram' : 'linkedin';
  const platform = platformList[0] || fallbackPlatform;
  const raw = enrichContentRow(row, batch, platformList.length ? platformList : [platform], publishSummary);
  const mediaPreview = contentMediaPreview(version, raw);
  const imageUrls = contentImageUrls(version, raw, mediaPreview);
  const generationSettled = ['ready', 'failed'].includes(item.generationStatus);
  const isGenerating = !generationSettled && item.generationStatus && item.status === 'generating';
  const status = item.archivedAt
    ? 'archived'
    : isGenerating
      ? 'generating'
      : publishSummaryStatus(publishSummary) || generationStatusToContentStatus(item.generationStatus) || item.status || 'draft';
  return {
    id: item.id,
    raw,
    title: item.title || version?.hook || item.topic || 'Untitled content',
    type: formatType(item.format),
    platform,
    platforms: platformList.length ? platformList : [platform],
    status,
    generationBatchId: item.generationBatchId || batch?.id || null,
    generationStage: item.generationStatus || (status === 'generating' ? 'queued' : null),
    generationProgress: Number(item.generationProgress ?? (status === 'generating' ? 5 : 100)),
    // Surface backend's per-job visual_status so tileStatus / preview can
    // detect "text done, image still cooking" — keeps a composing overlay
    // visible even after item.status flips to 'scheduled' / 'published'
    // for the text part.
    visualStatus: row?.jobVisualStatus ?? null,
    visualCompletedAt: row?.jobVisualCompletedAt ?? null,
    jobFormat: row?.jobFormat ?? null,
    date: formatDate(publishSummary?.lastPublishedAt || publishSummary?.lastFailedAt || publishSummary?.lastScheduledAt || item.updatedAt || item.createdAt),
    publishedAt: publishSummary?.lastPublishedAt || null,
    failedAt: publishSummary?.lastFailedAt || null,
    scheduledAt: publishSummary?.lastScheduledAt || item.publishScheduleAt || null,
    updatedAt: item.updatedAt,
    createdAt: item.createdAt,
    publishMeta: publishSummaryMeta(publishSummary),
    publishSummary,
    metrics: contentMetrics(raw, publishSummary),
    previewText: contentPreviewText(version, item),
    imageUrl: imageUrls[0] || mediaPreview.url,
    imageUrls,
    mediaPreview,
    batch,
  };
}

function generationStatusToContentStatus(status) {
  if (status === 'ready') return 'ready';
  if (status === 'failed') return 'failed';
  return null;
}

function enrichContentRow(row, batch, platforms, publishSummary) {
  return {
    ...row,
    generationBatch: batch || row.generationBatch || null,
    publishSummary: publishSummary || row.publishSummary || null,
    publishPlatforms: row.publishPlatforms || platforms,
    publishTargetAccountIds: row.publishTargetAccountIds || batch?.targetAccountIds || [],
    publishMode: row.publishMode || batch?.scheduleMode || 'draft',
    publishScheduleAt: row.publishScheduleAt || publishSummary?.lastScheduledAt || batch?.scheduleStartAt || null,
  };
}

function buildScheduleByContent(posts) {
  const map = new Map();
  posts.forEach(row => {
    const post = row.post || row;
    const contentId = post.contentItemId || row.content?.contentItem?.id;
    if (!contentId) return;
    const summary = map.get(contentId) || {
      platforms: [],
      publishedPlatforms: [],
      publishedCount: 0,
      failedCount: 0,
      activeCount: 0,
      lastPublishedAt: null,
      lastFailedAt: null,
      lastScheduledAt: null,
      scheduledPostId: null,
      postStatus: null,
      targets: [],
    };
    summary.scheduledPostId = summary.scheduledPostId || post.id;
    summary.postStatus = publishSummaryStatusFromPost(post.status, summary.postStatus);
    (row.targets || []).forEach(target => {
      if (!summary.platforms.includes(target.platform)) summary.platforms.push(target.platform);
      summary.targets.push(target);
      if (target.status === 'published') {
        summary.publishedCount += 1;
        if (!summary.publishedPlatforms.includes(target.platform)) summary.publishedPlatforms.push(target.platform);
      }
      if (['failed', 'needs_reconnect', 'needs_manual_action'].includes(target.status)) summary.failedCount += 1;
      if (['pending', 'queued', 'publishing'].includes(target.status) || ['planned', 'approved', 'queued'].includes(post.status)) summary.activeCount += 1;
      summary.lastPublishedAt = latestDate(summary.lastPublishedAt, target.publishedAt);
      if (['failed', 'needs_reconnect', 'needs_manual_action'].includes(target.status)) summary.lastFailedAt = latestDate(summary.lastFailedAt, target.failedAt || target.updatedAt);
      summary.lastScheduledAt = latestDate(summary.lastScheduledAt, target.scheduledAt || post.scheduledAt);
    });
    map.set(contentId, summary);
  });
  return map;
}

function publishSummaryStatus(summary) {
  if (!summary) return null;
  if (summary.failedCount > 0) return 'failed';
  if (['failed', 'partial_failure'].includes(summary.postStatus)) return 'failed';
  if (summary.postStatus === 'planned') return 'planned';
  if (publishSummaryIsPublishing(summary)) return 'publishing';
  if (summary.activeCount > 0) return 'scheduled';
  if (summary.targets?.length > 0) return summary.targets.every(target => target.status === 'published') ? 'published' : 'ready';
  if (summary.postStatus === 'published') return 'published';
  if (summary.postStatus === 'scheduled') return 'scheduled';
  return null;
}

function publishSummaryIsPublishing(summary) {
  const targets = Array.isArray(summary?.targets) ? summary.targets : [];
  return targets.some(target => ['queued', 'publishing'].includes(target.status))
    || targets.some(target => target.status === 'pending' && timestamp(target.scheduledAt) <= Date.now());
}

function preferredPlatforms(summary, version) {
  if (summary?.platforms?.length) return summary.platforms;
  if (summary?.publishedPlatforms?.length) return summary.publishedPlatforms;
  return Object.keys(version?.platformVersions || {}).filter(key => !key.startsWith('__'));
}

function contentMetrics(row, summary) {
  const targets = Array.isArray(summary?.targets) ? summary.targets : [];
  const publishedTargets = targets.filter(target => target.status === 'published');
  const metricTargets = publishedTargets.length ? publishedTargets : targets;
  const targetMetrics = sumMetrics(metricTargets);
  if (targetMetrics.available) return targetMetrics;

  const summaryMetrics = firstMetrics([summary, row?.publishSummary, row?.metrics, row?.analytics, row?.insights]);
  if (summaryMetrics.available) return summaryMetrics;

  return { views: 0, likes: 0, reactions: 0, comments: 0, viewsAvailable: false, viewsPending: false, available: false };
}

function sumMetrics(items) {
  return items.reduce((acc, item) => {
    const metrics = firstMetrics([item, item?.metrics, item?.analytics, item?.insights, item?.stats]);
    if (metrics.available) {
      if (metrics.viewsAvailable !== false) {
        acc.views += metrics.views;
        acc.viewsAvailable = true;
      } else if (metrics.viewsPending) {
        acc.viewsPending = true;
      }
      acc.likes += metrics.likes;
      acc.reactions += metrics.reactions;
      acc.comments += metrics.comments;
      acc.available = true;
    }
    return acc;
  }, { views: 0, likes: 0, reactions: 0, comments: 0, viewsAvailable: false, viewsPending: false, available: false });
}

function firstMetrics(sources) {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    const explicitViewsAvailable = typeof source.viewsAvailable === 'boolean' ? source.viewsAvailable : null;
    const viewsPending = source.viewsPending === true;
    const views = explicitViewsAvailable === false
      ? null
      : metricValue(source, ['views', 'viewCount', 'viewsCount', 'impressions', 'impressionCount', 'impressionsCount', 'reach', 'reachCount']);
    const likes = metricValue(source, ['likes', 'likeCount', 'likesCount']);
    const reactions = reactionMetricValue(source);
    const comments = metricValue(source, ['comments', 'commentCount', 'commentsCount', 'replies', 'replyCount', 'repliesCount']);
    if (views !== null || likes !== null || reactions !== null || comments !== null) {
      return { views: views || 0, likes: likes || 0, reactions: reactions || 0, comments: comments || 0, viewsAvailable: explicitViewsAvailable ?? views !== null, viewsPending, available: true };
    }
  }
  return { views: 0, likes: 0, reactions: 0, comments: 0, viewsAvailable: false, viewsPending: false, available: false };
}

function reactionMetricValue(source) {
  const direct = metricValue(source, ['reactionsTotal', 'reactions_total', 'reactionTotal', 'reaction_count', 'reactionCount', 'reactionsCount']);
  if (direct !== null) return direct;
  if (source.reactions && typeof source.reactions === 'object') {
    return metricValue(source.reactions, ['total', 'count', 'value']);
  }
  return metricValue(source, ['reactions']);
}

function metricValue(source, keys) {
  for (const key of keys) {
    if (source[key] === null || source[key] === undefined || source[key] === '') continue;
    const value = Number(source[key]);
    if (Number.isFinite(value) && value >= 0) return value;
  }
  return null;
}

function publishSummaryStatusFromPost(next, current) {
  if (current === 'failed' || ['failed', 'partial_failure'].includes(next)) return 'failed';
  if (current === 'scheduled' || ['approved', 'queued', 'scheduled'].includes(next)) return 'scheduled';
  if (current === 'published' || next === 'published') return 'published';
  if (current === 'planned' || next === 'planned') return 'planned';
  return current || next || null;
}

function publishSummaryMeta(summary) {
  if (!summary) return '';
  const platformText = summary.platforms.map(platformLabel).join(', ');
  if (summary.failedCount > 0 && summary.activeCount === 0) return `Failed on ${platformText}`;
  if (summary.postStatus === 'planned') return `Needs approval for ${platformText}`;
  if (publishSummaryIsPublishing(summary)) return `Publishing to ${platformText}`;
  if (summary.activeCount > 0) return `Scheduled for ${platformText}`;
  if (summary.targets?.length > 0 && summary.targets.every(target => target.status === 'published')) return `Published to ${platformText}`;
  if (!summary.targets?.length && summary.postStatus === 'published') return `Published to ${platformText}`;
  return platformText;
}

function contentPreviewText(version, item) {
  const platformVersions = version?.platformVersions || {};
  const platformText = Object.values(platformVersions).find(value => typeof value === 'string' && value.trim());
  const value = version?.caption || version?.body || version?.hook || platformText || item.topic || item.title || '';
  return String(value).replace(/\s+/g, ' ').trim() || 'Content from your workspace';
}

function contentMediaPreview(version, row) {
  const preview = row?.mediaPreview || version?.mediaPreview || row?.media_preview || version?.media_preview || null;
  const url = stringUrl(preview?.url || preview?.thumbnailUrl || preview?.imageUrl || preview?.src);
  const count = Number(preview?.count ?? preview?.mediaCount ?? row?.mediaCount ?? row?.media_count ?? 0);
  return {
    url,
    thumbnailUrl: stringUrl(preview?.thumbnailUrl) || url,
    count: Number.isFinite(count) && count > 0 ? count : (url ? 1 : 0),
    hasCarousel: Boolean(preview?.hasCarousel) || count > 1,
  };
}

function contentImageUrls(version, row, mediaPreview = null) {
  const seen = new Set();
  const out = [];
  const push = value => {
    if (typeof value !== 'string' || !/^https?:\/\//.test(value) || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  };
  push(mediaPreview?.url);
  push(mediaPreview?.thumbnailUrl);
  (row?.mediaUrls || row?.media_urls || []).forEach(push);
  (version?.mediaUrls || version?.media_urls || []).forEach(push);
  push(version?.imageUrl);
  push(version?.mediaUrl);
  push(version?.thumbnailUrl);
  push(version?.assetUrl);
  (row?.carouselSlides || row?.slides || []).forEach(slide => {
    push(slide.imageUrl);
    push(slide.publicUrl);
    push(slide.public_url);
    push(slide.mediaAsset?.publicUrl);
    push(slide.mediaAsset?.public_url);
  });
  Object.values(version?.platformVersions || {}).forEach(value => {
    if (value && typeof value === 'object') {
      push(value.imageUrl);
      push(value.mediaUrl);
      push(value.thumbnailUrl);
      push(value.assetUrl);
    }
  });
  return out;
}

function stringUrl(value) {
  return typeof value === 'string' && /^https?:\/\//.test(value) ? value : null;
}

function carouselPlaceholderUrl(index) {
  const label = encodeURIComponent(`Dathent carousel slide ${index}`);
  return `https://placehold.co/1080x1350/111827/FFFFFF/png?text=${label}`;
}

function latestDate(current, next) {
  if (!next) return current;
  if (!current) return next;
  return Date.parse(next) > Date.parse(current) ? next : current;
}

function platformLabel(platform) {
  const labels = { instagram: 'Instagram', threads: 'Threads', telegram: 'Telegram', x: 'X', twitter: 'X', tiktok: 'TikTok', linkedin: 'LinkedIn', facebook: 'Facebook' };
  return labels[platform] || platform;
}

function formatType(format) {
  const labels = { text_post: 'Text post', image_post: 'Image', carousel: 'Carousel', video: 'Video', story: 'Story' };
  return labels[format] || 'Content';
}

function formatDate(value) {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No date';
  return formatAppDate(date, { month: 'short', day: 'numeric' });
}
